import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../components/MainLayout';
import { supabase } from '../supabaseClient';
import { compressImage } from '../lib/imageUtils';
import { FiSave, FiUpload } from 'react-icons/fi';
import ErrorDialog from '../components/ErrorDialog';

export default function SettingsPage() {
    const navigate = useNavigate();
    const [restaurant, setRestaurant] = useState(null);
    const [form, setForm] = useState({ 
        name: '', phone: '', address: '', description: '', city: '', 
        tagline: '', cuisine: '', businessType: 'Cafe', gst: '', instagram: '',
        tableCount: 1, paymentPreference: 'Cash + Online'
    });
    const [isAccepting, setIsAccepting] = useState(true);
    const [whatsappReceipts, setWhatsappReceipts] = useState(false);
    const [checkoutFields, setCheckoutFields] = useState({ name: true, phone: true, table: false });
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [errorDialog, setErrorDialog] = useState({ isOpen: false, title: '', message: '' });
    // config state removed to fix unused warning

    const showError = (title, message) => setErrorDialog({ isOpen: true, title, message });

    useEffect(() => {
        async function loadRestaurant() {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return navigate('/login');
            const { data } = await supabase.from('restaurants').select('*').eq('owner_id', user.id).single();
            if (data) {
                setRestaurant(data);
                setForm({
                    name: data.name || '',
                    phone: data.phone || '',
                    address: data.address || '',
                    description: data.description || '',
                    city: data.city || '',
                    tagline: data.tagline || '',
                    cuisine: data.cuisine_type || '',
                    businessType: data.business_type || 'Cafe',
                    gst: data.tax_id || '',
                    instagram: data.social_links?.instagram || '',
                    tableCount: data.table_count || 1,
                    paymentPreference: data.payment_preference || 'Cash + Online',
                });
                setIsAccepting(data.is_accepting_orders ?? true);
                setWhatsappReceipts(data.whatsapp_receipts_enabled ?? false);
                setCheckoutFields(data.checkout_fields ?? { name: true, phone: true, table: false });
                setLogoPreview(data.logo_url || null);
            }
        }
        loadRestaurant();
    }, [navigate]);

    const handleLogoChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 2 * 1024 * 1024) return showError('File Too Large', 'Logo must be under 2MB.');
        setLogoFile(file);
        setLogoPreview(URL.createObjectURL(file));
    };

    const uploadLogo = async () => {
        if (!logoFile) return restaurant?.logo_url || null;
        setUploadingLogo(true);
        try {
            const compressed = await compressImage(logoFile, { maxWidthOrHeight: 800 });
            const ext = compressed.name.split('.').pop() || 'jpg';
            const filePath = `logos/${restaurant.id}.${ext}`;
            const { error: uploadErr } = await supabase.storage.from('menu-images').upload(filePath, compressed, { upsert: true });
            if (uploadErr) throw uploadErr;
            const { data: urlData } = supabase.storage.from('menu-images').getPublicUrl(filePath);
            return urlData?.publicUrl || null;
        } catch (err) {
            showError('Upload Failed', 'Failed to upload logo: ' + err.message);
            return restaurant?.logo_url || null;
        } finally {
            setUploadingLogo(false);
        }
    };

    const handleSave = async (e) => {
        if (e?.preventDefault) e.preventDefault();
        if (!restaurant) return;
        if (!form.name.trim()) return showError('Validation Error', 'Restaurant Name is required.');
        if (form.phone && !/^\d{10}$/.test(form.phone)) return showError('Validation Error', 'Contact Number must be exactly 10 digits.');

        setSaving(true);
        const logoUrl = await uploadLogo();

        const { error } = await supabase.from('restaurants').update({
            name: form.name.trim(),
            phone: form.phone.trim(),
            address: form.address.trim(),
            description: form.description.trim(),
            city: form.city.trim(),
            tagline: form.tagline.trim(),
            cuisine_type: form.cuisine.trim(),
            business_type: form.businessType,
            tax_id: form.gst.trim(),
            social_links: { ...(restaurant?.social_links || {}), instagram: form.instagram.trim() },
            logo_url: logoUrl,
            table_count: parseInt(form.tableCount) || 1,
            payment_preference: form.paymentPreference,
            whatsapp_receipts_enabled: whatsappReceipts,
            checkout_fields: checkoutFields,
            updated_at: new Date().toISOString(),
        }).eq('id', restaurant.id);

        setSaving(false);
        if (error) return showError('Save Failed', error.message);
        
        setRestaurant(prev => ({ 
            ...prev, 
            name: form.name.trim(),
            phone: form.phone.trim(),
            address: form.address.trim(),
            description: form.description.trim(),
            city: form.city.trim(),
            tagline: form.tagline.trim(),
            cuisine_type: form.cuisine.trim(),
            business_type: form.businessType,
            tax_id: form.gst.trim(),
            social_links: { ...(prev?.social_links || {}), instagram: form.instagram.trim() },
            logo_url: logoUrl, 
            table_count: parseInt(form.tableCount) || 1,
            payment_preference: form.paymentPreference,
            whatsapp_receipts_enabled: whatsappReceipts, 
            checkout_fields: checkoutFields 
        }));
        
        setLogoFile(null);
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
    };



    const handleToggleAccepting = async () => {
        const newVal = !isAccepting;
        setIsAccepting(newVal);
        try {
            const { error } = await supabase.from('restaurants').update({ is_accepting_orders: newVal }).eq('id', restaurant.id);
            if (error) throw error;
        } catch (err) {
            console.error(err);
            setIsAccepting(!newVal);
            showError('Update Failed', 'Could not update order status. Please try again.');
        }
    };

    async function handleLogout() {
        await supabase.auth.signOut();
        navigate('/login', { replace: true });
    }

    const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

    return (
        <MainLayout activeLink="Settings" title="Restaurant Settings">
            <main className="flex-1 p-6 lg:p-10 max-w-4xl mx-auto w-full">
                <header className="mb-10 flex items-center justify-between">
                    <div>
                        <div className="flex items-center gap-3 mb-2">
                            <h1 className="text-3xl font-black tracking-tight text-slate-900 dark:text-white">Restaurant Settings</h1>
                            <div className="flex items-center gap-2 px-3 py-1 bg-emerald-500/10 rounded-full border border-emerald-500/20">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                                <span className="text-[10px] font-black uppercase tracking-widest text-emerald-600 dark:text-emerald-400">System Live</span>
                            </div>
                        </div>
                        <p className="text-slate-500 dark:text-slate-400 font-medium">Configure your restaurant's digital presence and operational parameters.</p>
                    </div>
                </header>

                {/* General Info */}
                <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] border border-slate-100 dark:border-slate-800 p-1 sm:p-2 shadow-xl shadow-slate-200/50 dark:shadow-none mb-8 group transition-all hover:border-primary/30">
                    <div className="p-6 sm:p-8">
                        <div className="flex items-center gap-4 mb-10">
                            <div className="w-12 h-12 rounded-2xl bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center text-2xl shadow-inner">ℹ️</div>
                            <div>
                                <h2 className="text-xl font-black text-slate-900 dark:text-white tracking-tight">General Information</h2>
                                <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">Core details about your restaurant business</p>
                            </div>
                        </div>
                    <form onSubmit={handleSave} className="space-y-8">
                        {/* Logo Section */}
                        <div className="bg-slate-50/50 dark:bg-slate-800/40 p-6 rounded-3xl border border-slate-100 dark:border-slate-700/50 mb-10">
                            <label htmlFor="logo-upload" className="block text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500 mb-6">Visual Identity</label>
                            <div className="flex flex-col sm:flex-row items-center gap-8">
                                <button type="button" className="w-24 h-24 rounded-[2.5rem] bg-white dark:bg-slate-900 flex items-center justify-center overflow-hidden border-2 border-slate-100 dark:border-slate-800 cursor-pointer relative group transition-all hover:scale-105 active:scale-95 hover:border-primary shadow-lg" onClick={() => document.getElementById('logo-upload').click()} aria-label="Change logo">
                                    {logoPreview ? (
                                        <img src={logoPreview} alt="Logo preview" className="w-full h-full object-cover" />
                                    ) : (
                                        <span className="text-primary text-4xl font-black">{form.name?.[0] || 'R'}</span>
                                    )}
                                    <div className="absolute inset-0 bg-slate-900/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity backdrop-blur-sm">
                                        <FiUpload className="text-white w-6 h-6" />
                                    </div>
                                </button>
                                <div className="space-y-3 text-center sm:text-left">
                                    <label htmlFor="logo-upload" className="inline-flex items-center gap-2 bg-slate-900 dark:bg-primary text-white dark:text-charcoal px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest cursor-pointer transition-all hover:shadow-lg active:scale-95">
                                        <FiUpload className="w-3.5 h-3.5" />
                                        {uploadingLogo ? 'Processing...' : 'Upload New Logo'}
                                        <input id="logo-upload" type="file" className="hidden" accept="image/*" onChange={handleLogoChange} />
                                    </label>
                                    <p className="text-[11px] text-slate-400 font-medium max-w-[200px]">Best in 1:1 ratio. PNG or JPG. Max 2MB.</p>
                                </div>
                            </div>
                        </div>

                        {/* Name & Phone */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <label htmlFor="res-name" className="block text-sm font-bold text-slate-700 dark:text-slate-300">Restaurant Name *</label>
                                <input id="res-name" type="text" value={form.name} onChange={e => set('name', e.target.value)} required className="input-field dark:bg-slate-800 dark:border-slate-700 dark:text-white" placeholder="e.g. Green Bistro & Deli" />
                            </div>
                            <div className="space-y-2">
                                <label htmlFor="res-phone" className="block text-sm font-bold text-slate-700 dark:text-slate-300">Contact Number</label>
                                <input id="res-phone" type="tel" value={form.phone} onChange={e => set('phone', e.target.value.replace(/\D/g, '').slice(0, 10))} className="input-field dark:bg-slate-800 dark:border-slate-700 dark:text-white" placeholder="10-digit mobile number" maxLength={10} />
                            </div>
                        </div>

                        {/* City & Address */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <label htmlFor="res-city" className="block text-sm font-bold text-slate-700 dark:text-slate-300">City</label>
                                <input id="res-city" type="text" value={form.city} onChange={e => set('city', e.target.value)} className="input-field dark:bg-slate-800 dark:border-slate-700 dark:text-white" placeholder="e.g. Mumbai" />
                            </div>
                            <div className="space-y-2">
                                <label htmlFor="res-address" className="block text-sm font-bold text-slate-700 dark:text-slate-300">Store Address</label>
                                <input id="res-address" type="text" value={form.address} onChange={e => set('address', e.target.value)} className="input-field dark:bg-slate-800 dark:border-slate-700 dark:text-white" placeholder="123 Main Street, City" />
                            </div>
                        </div>

                        {/* Branding & Socials */}
                        <div className="bg-slate-50/50 dark:bg-slate-800/40 p-6 rounded-2xl border border-slate-100 dark:border-slate-700/50 space-y-6">
                            <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500">Branding & Identity</h3>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="col-span-2 space-y-2">
                                    <label htmlFor="res-tagline" className="block text-sm font-bold text-slate-700 dark:text-slate-300">Brand Tagline</label>
                                    <input id="res-tagline" type="text" value={form.tagline} onChange={e => set('tagline', e.target.value)} className="input-field dark:bg-slate-800 dark:border-slate-700 dark:text-white" placeholder="e.g. Authentic Punjabi Experience" />
                                </div>
                                <div className="space-y-2">
                                    <label htmlFor="res-type" className="block text-sm font-bold text-slate-700 dark:text-slate-300">Business Type</label>
                                    <select id="res-type" value={form.businessType} onChange={e => set('businessType', e.target.value)} className="input-field dark:bg-slate-800 dark:border-slate-700 dark:text-white">
                                        <option>Cafe</option>
                                        <option>Fine Dine</option>
                                        <option>QSR</option>
                                        <option>Bar / Pub</option>
                                    </select>
                                </div>
                                <div className="space-y-2">
                                    <label htmlFor="res-cuisine" className="block text-sm font-bold text-slate-700 dark:text-slate-300">Main Cuisine</label>
                                    <input id="res-cuisine" type="text" value={form.cuisine} onChange={e => set('cuisine', e.target.value)} className="input-field dark:bg-slate-800 dark:border-slate-700 dark:text-white" placeholder="e.g. Italian, North Indian" />
                                </div>
                                <div className="space-y-2">
                                    <label htmlFor="res-gst" className="block text-sm font-bold text-slate-700 dark:text-slate-300">GST Number (Tax ID)</label>
                                    <input id="res-gst" type="text" value={form.gst} onChange={e => set('gst', e.target.value.toUpperCase())} className="input-field uppercase dark:bg-slate-800 dark:border-slate-700 dark:text-white" placeholder="GSTIN Number" />
                                </div>
                                <div className="space-y-2">
                                    <label htmlFor="res-insta" className="block text-sm font-bold text-slate-700 dark:text-slate-300">Instagram @Handle</label>
                                    <input id="res-insta" type="text" value={form.instagram} onChange={e => set('instagram', e.target.value)} className="input-field dark:bg-slate-800 dark:border-slate-700 dark:text-white" placeholder="e.g. punjabi_tadka" />
                                </div>
                                <div className="space-y-2">
                                    <label htmlFor="res-tables" className="block text-sm font-bold text-slate-700 dark:text-slate-300">Number of Tables *</label>
                                    <input id="res-tables" type="number" min="1" value={form.tableCount} onChange={e => set('tableCount', e.target.value)} className="input-field dark:bg-slate-800 dark:border-slate-700 dark:text-white" placeholder="e.g. 10" />
                                </div>
                                <div className="space-y-2">
                                    <label htmlFor="res-payment" className="block text-sm font-bold text-slate-700 dark:text-slate-300">Payment Preference *</label>
                                    <select id="res-payment" value={form.paymentPreference} onChange={e => set('paymentPreference', e.target.value)} className="input-field dark:bg-slate-800 dark:border-slate-700 dark:text-white">
                                        <option>Cash + Online</option>
                                        <option>Online Payments Only</option>
                                        <option>Pay at Counter</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* Description */}
                        <div className="space-y-2">
                            <label htmlFor="res-desc" className="block text-sm font-bold text-slate-700 dark:text-slate-300">Description</label>
                            <textarea id="res-desc" rows="3" value={form.description} onChange={e => set('description', e.target.value)} className="input-field dark:bg-slate-800 dark:border-slate-700 dark:text-white resize-none" placeholder="Tell your customers about your restaurant..." />
                        </div>

                        {/* Actions */}
                        <div className="flex items-center justify-between pt-10 border-t border-slate-100 dark:border-slate-800">
                            <button type="button" onClick={handleLogout} className="text-[10px] font-black text-slate-400 hover:text-red-500 transition-all uppercase tracking-[0.2em] px-4 py-2 hover:bg-red-50 dark:hover:bg-red-900/10 rounded-xl active:scale-95">Terminate Session</button>
                            <div className="flex items-center gap-6">
                                {saved && <span className="text-emerald-500 text-[10px] font-black uppercase tracking-[0.2em] animate-bounce">✓ Changes Saved Successfully</span>}
                                <button type="submit" disabled={saving || uploadingLogo} className="bg-slate-900 dark:bg-primary text-white dark:text-charcoal px-10 py-4 rounded-2xl text-xs font-black uppercase tracking-[0.2em] shadow-xl shadow-slate-200 dark:shadow-primary/10 transition-all hover:-translate-y-1 active:scale-95 disabled:opacity-50 hidden sm:flex items-center gap-2">
                                    {(saving || uploadingLogo) ? <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" /> : <FiSave className="w-4 h-4" />}
                                    Commit Changes
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>

            {/* Accepting Orders Toggle */}
                <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] border border-slate-100 dark:border-slate-800 p-6 sm:p-8 shadow-xl shadow-slate-200/40 dark:shadow-none mb-8 transition-all hover:scale-[1.01]">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-6">
                            <div className={`w-14 h-14 rounded-[1.5rem] flex items-center justify-center transition-all duration-500 ${isAccepting ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20 shadow-inner' : 'bg-slate-100 text-slate-400 dark:bg-slate-800'}`}>
                                <span className="material-symbols-outlined text-3xl">{isAccepting ? 'storefront' : 'store'}</span>
                            </div>
                            <div>
                                <h3 className={`font-black uppercase tracking-[0.2em] text-[10px] mb-2 ${isAccepting ? 'text-emerald-600' : 'text-slate-400'}`}>
                                    {isAccepting ? 'System Operational' : 'Orders Suspended'}
                                </h3>
                                <p className="text-sm text-slate-600 dark:text-slate-300 font-black tracking-tight">
                                    Your QR menu is currently <span className={isAccepting ? 'text-emerald-500' : 'text-slate-500'}>{isAccepting ? 'Live & Accepting Orders' : 'Offline'}</span>
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={handleToggleAccepting}
                            aria-pressed={isAccepting}
                            aria-label={isAccepting ? 'Stop accepting orders' : 'Start accepting orders'}
                            className={`relative inline-flex h-9 w-16 items-center rounded-full transition-all duration-500 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary/20 ${isAccepting ? 'bg-emerald-500 shadow-[0_0_20px_rgba(16,185,129,0.4)]' : 'bg-slate-300 dark:bg-slate-700'}`}
                        >
                            <span className={`inline-block h-7 w-7 transform rounded-full bg-white shadow-2xl transition-transform duration-500 ease-out ${isAccepting ? 'translate-x-8' : 'translate-x-1'}`} />
                        </button>
                    </div>
                </div>

                {/* WhatsApp Receipts Toggle */}
                <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] border border-slate-100 dark:border-slate-800 p-6 sm:p-8 shadow-xl shadow-slate-200/40 dark:shadow-none mb-8 transition-all hover:scale-[1.01]">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-6">
                            <div className={`w-14 h-14 rounded-[1.5rem] flex items-center justify-center transition-all duration-500 ${whatsappReceipts ? 'bg-[#25D366]/10 text-[#25D366] shadow-inner' : 'bg-slate-100 text-slate-400 dark:bg-slate-800'}`}>
                                <svg className="w-7 h-7 fill-current" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>
                            </div>
                            <div>
                                <h3 className={`font-black uppercase tracking-[0.2em] text-[10px] mb-2 ${whatsappReceipts ? 'text-[#25D366]' : 'text-slate-400'}`}>
                                    WhatsApp Integration
                                </h3>
                                <p className="text-sm text-slate-600 dark:text-slate-300 font-black tracking-tight">
                                    WhatsApp Order <span className={whatsappReceipts ? 'text-[#25D366]' : 'text-slate-500'}>{whatsappReceipts ? 'Receipts Enabled' : 'Disabled'}</span>
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={async () => {
                                const newVal = !whatsappReceipts;
                                setWhatsappReceipts(newVal);
                                try {
                                    await supabase.from('restaurants').update({ whatsapp_receipts_enabled: newVal }).eq('id', restaurant.id);
                                } catch (e) {
                                    console.error(e);
                                    setWhatsappReceipts(!newVal);
                                }
                            }}
                            aria-pressed={whatsappReceipts}
                            aria-label={whatsappReceipts ? 'Disable WhatsApp receipts' : 'Enable WhatsApp receipts'}
                            className={`relative inline-flex h-9 w-16 items-center rounded-full transition-all duration-500 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#25D366]/20 ${whatsappReceipts ? 'bg-[#25D366] shadow-[0_0_20px_rgba(37,211,102,0.4)]' : 'bg-slate-300 dark:bg-slate-700'}`}
                        >
                            <span className={`inline-block h-7 w-7 transform rounded-full bg-white shadow-2xl transition-transform duration-500 ease-out ${whatsappReceipts ? 'translate-x-8' : 'translate-x-1'}`} />
                        </button>
                    </div>
                </div>

                {/* Customer Checkout Fields */}
                <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] border border-slate-100 dark:border-slate-800 p-6 sm:p-8 shadow-xl shadow-slate-200/40 dark:shadow-none mb-8">
                    <div className="flex items-center gap-4 mb-8">
                        <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center text-2xl shadow-inner">📝</div>
                        <div>
                            <h2 className="text-xl font-black text-slate-900 dark:text-white tracking-tight">Checkout Experience</h2>
                            <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">Customize which details customers provide during order</p>
                        </div>
                    </div>
                    <div className="space-y-4">
                        {[
                            { key: 'name', label: 'Customer Name', desc: 'Personalize the experience with names' },
                            { key: 'phone', label: 'Mobile Number', desc: 'Required for digital receipts & updates' },
                            { key: 'table', label: 'Table Reference', desc: 'Identify order location automatically' },
                        ].map(field => (
                            <div key={field.key} className="flex items-center justify-between p-5 bg-slate-50/50 dark:bg-slate-800/30 rounded-[1.5rem] border border-slate-100 dark:border-slate-800/50 transition-all hover:bg-white dark:hover:bg-slate-800 hover:shadow-md group">
                                <div className="flex items-center gap-4">
                                    <div className={`w-2 h-2 rounded-full transition-all ${checkoutFields[field.key] ? 'bg-primary animate-pulse' : 'bg-slate-300 dark:bg-slate-700'}`} />
                                    <div>
                                        <h4 className="text-sm font-black text-slate-800 dark:text-slate-200 uppercase tracking-widest">{field.label}</h4>
                                        <p className="text-xs text-slate-500 dark:text-slate-400 font-medium mt-0.5">{field.desc}</p>
                                    </div>
                                </div>
                                <button
                                    onClick={async () => {
                                        const updated = { ...checkoutFields, [field.key]: !checkoutFields[field.key] };
                                        setCheckoutFields(updated);
                                        try {
                                            await supabase.from('restaurants').update({ checkout_fields: updated }).eq('id', restaurant.id);
                                        } catch (e) {
                                            console.error(e);
                                            setCheckoutFields(checkoutFields);
                                        }
                                    }}
                                    aria-pressed={checkoutFields[field.key]}
                                    aria-label={`Toggle ${field.label}`}
                                    className={`relative inline-flex h-8 w-14 items-center rounded-full transition-all duration-300 ${checkoutFields[field.key] ? 'bg-primary' : 'bg-slate-300 dark:bg-slate-700'}`}
                                >
                                    <span className={`inline-block h-6 w-6 transform rounded-full bg-white shadow-xl transition-transform duration-300 ${checkoutFields[field.key] ? 'translate-x-7' : 'translate-x-1'}`} />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>

                {/* --- Subscription & Plan --- */}
                <div className="bg-white dark:bg-slate-900 rounded-[2rem] border-2 border-primary/20 p-6 shadow-sm mb-6 transition-all hover:border-primary/40">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center">
                                <span className="material-symbols-outlined text-primary text-3xl">credit_card</span>
                            </div>
                            <div>
                                <h3 className="font-black text-slate-900 dark:text-white uppercase tracking-widest text-sm mb-1">My Plan & Subscription</h3>
                                <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">Manage your billing, upgrade plan, and view usage limits</p>
                            </div>
                        </div>
                        <button 
                            onClick={() => navigate('/subscription')}
                            className="bg-primary text-charcoal font-black text-xs px-6 py-3 rounded-xl hover:bg-primary/80 transition-all shadow-lg active:scale-95"
                        >
                            Manage Plan
                        </button>
                    </div>
                </div>
            </main>

                {/* Mobile Sticky Save Bar */}
                <div className="fixed bottom-0 left-0 right-0 p-4 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border-t border-slate-100 dark:border-slate-800 sm:hidden z-50 animate-slide-up">
                    <button 
                        onClick={handleSave}
                        disabled={saving || uploadingLogo}
                        className="w-full bg-slate-900 dark:bg-primary text-white dark:text-charcoal py-4 rounded-2xl font-black uppercase tracking-[0.2em] shadow-2xl flex items-center justify-center gap-3 active:scale-95 transition-all"
                    >
                        {(saving || uploadingLogo) ? <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" /> : <FiSave className="w-5 h-5" />}
                        {saved ? 'Changes Saved' : 'Commit Changes'}
                    </button>
                </div>

            <div className="fixed top-0 right-0 -z-10 w-[500px] h-[500px] bg-primary/5 blur-[120px] rounded-full pointer-events-none" />
            <ErrorDialog
                isOpen={errorDialog.isOpen}
                title={errorDialog.title}
                message={errorDialog.message}
                onClose={() => setErrorDialog({ ...errorDialog, isOpen: false })}
            />
        </MainLayout>
    );
}
